import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Input from 'shopper/components/Input';
import Select from 'shopper/components/Select';

import FormHeading from 'components/SuggestNewItem/FormHeading';
import TextEditor from 'components/TextEditor';

import useQueryClient from 'hooks/useQueryClient';

import { sendEvent } from 'lib/gtag';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setLocalStorage,
} from 'lib/localStorage';
import placeholder from 'lib/placeholder';
import { storeQueryKeys } from 'lib/queryKeys';
import { isUrlValid } from 'lib/string';
import { noop } from 'lib/utils';

import { useUser } from 'providers/UserProvider';

import { postNewCoupon } from 'services/coupon';
import { getStoresList } from 'services/store';

import { TOOLS } from 'constants/textEditor';

const FORM_ERROR_MESSAGES = {
  COUPON_CODE_REQUIRED: 'Digite o código do cupom',
  COUPON_TITLE_REQUIRED: 'Digite a descrição do cupom',
  INVALID_COUPON_URL: 'Insira uma URL válida.',
  STORE_ID_REQUIRED: 'Selecione a loja em que o cupom é válido',
};
const RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY = 'coupon.form-recovery';

const isCouponFromStore = (couponUrl, storePageUrl) =>
  couponUrl?.includes(storePageUrl);

const toFormattedStoreOptions = (stores, isSelectedCallback = () => false) =>
  stores.map(({ pageId, pageTitle, pageFrom, ...rest }) => ({
    label: pageTitle,
    value: String(pageId),
    isSelected: isSelectedCallback({ pageId, pageTitle, pageFrom, ...rest }),
    pageFrom,
  }));

const SuggestNewCouponForm = ({
  className,
  gaEventCategory,
  onSubmitSuccess = noop,
  ...rest
}) => {
  const { isLoggedIn, runCallbackIfLoggedIn } = useUser();
  const queryClient = useQueryClient();
  const [
    couponUrlOrStoreIdHasBeenTouchedAtLeastOneTime,
    setCouponUrlOrStoreIdHasBeenTouchedAtLeastOneTime,
  ] = useState(true);
  const [isFormSubmitEnabled, setFormSubmitEnabled] = useState(false);
  const [recoveryFormData, setRecoveryFormData] = useState(null);
  const {
    control,
    formState,
    clearErrors,
    reset: formReset,
    getValues,
    handleSubmit,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      couponCode: '',
      couponInstructions: '',
      couponTitle: '',
      couponUrl: '',
      storeId: '',
    },
    mode: 'onChange',
    reValidateMode: 'onBlur',
  });

  const { mutate: mutateSuggestNewCoupon, reset } = useMutation({
    mutationFn: postNewCoupon,
    onSuccess: () => {
      removeFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY);
      reset();
      formReset();
      onSubmitSuccess();
    },
  });

  const { data: storesOptions = [] } = useQuery({
    queryKey: storeQueryKeys.list(),
    queryFn: ({ signal }) => getStoresList(undefined, { signal }),
    select: (data) => toFormattedStoreOptions(data),
    staleTime: Infinity,
  });

  useEffect(() => {
    const data = getFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY);

    if (data) {
      setRecoveryFormData(JSON.parse(data));
    }

    return () => {
      removeFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY);
      queryClient.removeQueries(storeQueryKeys.list());
    };
  }, []);

  /**
   * Because of the behavior of `couponUrl` after being filled can fill the
   * `storeId` field, to avoid inconsistencies, we need to manually check if
   * the required fields are valid.
   */
  useEffect(() => {
    if (formState.isSubmitting || formState.isSubmitted) {
      setFormSubmitEnabled(false);
      return;
    }

    const formHasError = Object.values(formState.errors).length;
    const isRequiredFieldsFilled = Object.values(
      getValues(['couponCode', 'couponTitle', 'storeId'])
    ).every(Boolean);

    if (!formHasError && isRequiredFieldsFilled) {
      if (!isFormSubmitEnabled) {
        sendEvent({
          category: gaEventCategory,
          action: 'enabled_submit',
        });
        setFormSubmitEnabled(true);
      }
    } else {
      setFormSubmitEnabled(false);
    }
  }, [watch(['couponCode', 'couponTitle', 'storeId'])]);

  /**
   * To prevent undue error messages when the `storeId` aren't filled at least
   * one time, we uses the state of
   * `couponUrlOrStoreIdHasBeenTouchedAtLeastOneTime` to don't show these errors
   * when the fields of `storeId` or `couponUrl` aren't touched (focused) at
   * least one time.
   */
  useEffect(() => {
    const { touchedFields } = formState;

    setCouponUrlOrStoreIdHasBeenTouchedAtLeastOneTime(
      () => !!(touchedFields.storeId || touchedFields.couponUrl)
    );
  }, [formState]);

  /**
   * To behavior of `couponUrl` can be fill the `storeId` field to exist, we
   * need to remap the storeOptions to has only one store selected, manually
   * set the newest storeId value in form state and when the `couponUrl`
   * is cleaned, clean the `storeId` and revalidate this field.
   */
  useEffect(() => {
    if (!getValues('couponUrl')) {
      if (
        getValues('storeId') &&
        !getFromLocalStorage(RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY)
      ) {
        setValue('storeId', '', { shouldValidate: true });
      }

      return;
    }

    const mappedOptions = storesOptions.map(({ pageFrom, ...props }) => ({
      ...props,
      pageFrom,
      isSelected: isCouponFromStore(getValues('couponUrl'), pageFrom),
    }));
    const selectedStore = mappedOptions.find(({ isSelected }) => isSelected);

    if (selectedStore?.value) {
      setValue('storeId', selectedStore?.value);
    }
  }, [watch('couponUrl')]);

  /**
   * When `couponUrl` fills the `storeId` field that has any previous error,
   * these errors aren't be cleaned. Because this, we need to
   * manually clean this errors.
   */
  useEffect(() => {
    if (getValues('storeId') && formState.errors?.storeId?.message) {
      clearErrors('storeId');
    }
  }, [watch('storeId')]);

  const onSubmit = (newCouponData) => {
    sendEvent({ action: 'submit_attempt', category: gaEventCategory });

    setLocalStorage(
      RECOVERY_FORM_DATA_LOCAL_STORAGE_KEY,
      JSON.stringify(newCouponData)
    );

    runCallbackIfLoggedIn(() => {
      mutateSuggestNewCoupon(newCouponData);
    });
  };

  useEffect(() => {
    if (recoveryFormData && isLoggedIn) {
      onSubmit(recoveryFormData);
    }
  }, [isLoggedIn]);

  return (
    <form
      className={twMerge('px-4', className)}
      onSubmit={handleSubmit(onSubmit)}
      {...rest}
    >
      <FormHeading
        className="mb-6 mt-4 md:mt-6"
        description={placeholder('descriptions.suggestCoupon')}
        inputProps={{
          control,
          label: placeholder('inputs.labels.couponUrl'),
          name: 'couponUrl',
          placeholder: placeholder('inputs.placeholders.couponUrl'),
          rules: {
            validate: {
              validateUrl: (value) =>
                value.length === 0 ||
                isUrlValid(value) ||
                FORM_ERROR_MESSAGES.INVALID_COUPON_URL,
            },
          },
          onFocus: () =>
            sendEvent({ category: gaEventCategory, action: 'focus_link' }),
        }}
        title={placeholder('titles.suggestCoupon')}
      />
      <Box className="px-4">
        <Controller
          control={control}
          name="couponTitle"
          render={({ field, fieldState: { error } }) => (
            <Input
              error={!!error}
              label={placeholder('inputs.labels.couponTitle')}
              message={error?.message}
              placeholder={placeholder('inputs.placeholders.couponTitle')}
              onFocus={() =>
                sendEvent({ category: gaEventCategory, action: 'focus_title' })
              }
              {...field}
              full
              required
            />
          )}
          rules={{
            required: FORM_ERROR_MESSAGES.COUPON_TITLE_REQUIRED,
          }}
        />
        <div className="my-6 flex flex-col md:flex-row">
          <Controller
            control={control}
            name="storeId"
            render={({ field, fieldState: { error } }) => (
              <Select
                className="mr-4"
                error={
                  couponUrlOrStoreIdHasBeenTouchedAtLeastOneTime && !!error
                }
                internalValueState={false}
                message={error?.message}
                name="storeId"
                options={storesOptions}
                placeholder={placeholder('inputs.placeholders.couponStore')}
                topLabel={placeholder('inputs.labels.couponStore')}
                full
                required
                {...field}
              />
            )}
            rules={{
              validate: {
                validateValue: (value) =>
                  !couponUrlOrStoreIdHasBeenTouchedAtLeastOneTime ||
                  !!value ||
                  FORM_ERROR_MESSAGES.STORE_ID_REQUIRED,
              },
            }}
          />
          <Controller
            control={control}
            name="couponCode"
            render={({ field, fieldState: { error } }) => (
              <Input
                className="mt-6 md:ml-4 md:mt-0"
                error={!!error}
                label={placeholder('inputs.labels.couponCode')}
                message={error?.message}
                placeholder={placeholder('inputs.placeholders.couponCode')}
                full
                required
                onFocus={() => {
                  sendEvent({
                    category: gaEventCategory,
                    action: 'focus_code',
                  });
                }}
                {...field}
              />
            )}
            rules={{
              required: FORM_ERROR_MESSAGES.COUPON_CODE_REQUIRED,
            }}
          />
        </div>
        <Controller
          control={control}
          name="couponInstructions"
          render={({ field: { onChange, ...fieldProps } }) => (
            <TextEditor
              className="mt-2"
              label={placeholder('inputs.labels.additionalInfos')}
              placeholder={placeholder(
                'inputs.placeholders.additionalCouponInfos'
              )}
              toolbarItems={[
                TOOLS.EMBEDDED_IMAGE,
                TOOLS.EMBEDDED_VIDEO,
                TOOLS.EMOJI,
              ]}
              onChange={(editorState) =>
                onChange(editorState.getContentInHtml())
              }
              onFocus={() => {
                sendEvent({
                  category: gaEventCategory,
                  action: 'focus_instructions',
                });
              }}
              {...fieldProps}
            />
          )}
        />
        <Button
          className="mt-6 self-end md:w-unset"
          disabled={!isFormSubmitEnabled}
          htmlType="submit"
          full
        >
          {placeholder('actions.sendCoupon')}
        </Button>
      </Box>
    </form>
  );
};

SuggestNewCouponForm.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  onSubmitSuccess: PropTypes.func,
};

export default SuggestNewCouponForm;
